"use client";

import { ExternalLinkIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type BlockScoutSponsorModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Extracts the tweet ID from a Twitter/X URL
 */
function extractTweetId(url: string): string | null {
  // Handle various Twitter URL formats:
  // https://twitter.com/username/status/1234567890
  // https://x.com/username/status/1234567890
  const match = url.match(/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/);
  return match?.at(1) ?? null;
}

/**
 * Generates a Twitter intent URL for replying to a specific tweet
 */
function generateTweetReplyUrl(
  originalTweetUrl: string,
  replyText: string
): string {
  const tweetId = extractTweetId(originalTweetUrl);

  if (tweetId) {
    // Create a reply to the specific tweet
    return `https://twitter.com/intent/tweet?in_reply_to=${tweetId}&text=${encodeURIComponent(replyText)}`;
  }

  // Fallback: just create a new tweet mentioning @blockscout
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(replyText)}`;
}

const SPONSOR_TWEET_URL = process.env.NEXT_PUBLIC_BLOCKSCOUT_SPONSOR_TWEET_URL;

const DEFAULT_TWEET_TEXT = `Hey @blockscout! Please consider sponsoring txgpt.xyz - it's an awesome free tool for exploring blockchain data powered by your API! 🙏

#BlockScout #Web3 #Blockchain`;

export function BlockScoutSponsorModal({
  open,
  onOpenChange,
}: BlockScoutSponsorModalProps) {
  const handleTweetClick = () => {
    const tweetUrl = SPONSOR_TWEET_URL
      ? generateTweetReplyUrl(SPONSOR_TWEET_URL, DEFAULT_TWEET_TEXT)
      : `https://twitter.com/intent/tweet?text=${encodeURIComponent(DEFAULT_TWEET_TEXT)}`;

    window.open(tweetUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl">
            Help Keep <span className="text-primary">txgpt.xyz</span> Free
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                We&apos;ve hit the{" "}
                <span className="font-semibold text-foreground">
                  BlockScout API rate limit
                </span>
                . txgpt.xyz uses BlockScout&apos;s blockchain explorer API to
                fetch transaction data, but our{" "}
                <span className="text-orange-400">free tier</span> has limited
                requests.
              </p>
              <p>
                Want to help?{" "}
                <span className="font-semibold text-foreground">
                  Tweet your support
                </span>{" "}
                to encourage{" "}
                <span className="font-semibold text-primary">@blockscout</span>{" "}
                to sponsor our API access. This will help keep txgpt.xyz{" "}
                <span className="font-semibold text-green-400">
                  free for everyone
                </span>
                !
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          <AlertDialogAction onClick={handleTweetClick}>
            <ExternalLinkIcon className="mr-2 size-4" />
            Tweet Support
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
